/**
 * ShareTrackTab — copy-paste-ready share links + embed snippets, every
 * one pre-tagged with the correct UTM tuple (source / medium / campaign
 * / content / id) so the Widget Performance dashboard can attribute
 * every paid sale to the channel that drove it.
 *
 * Lives inside EventMarketing.js as the new "Share & Track" tab.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Link as LinkIcon, Code as CodeIcon, Copy, Check, Trash2, Plus,
  Sparkles, Mail, Instagram, Facebook, Twitter, Music2, MessageSquare,
  Users, Tag, Loader2, BookOpen, ChevronDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map known channel keywords -> lucide icon component. Returns the
// rendered element so the parent never holds a "dynamic" component
// reference (keeps react-hooks/static-components happy).
const renderChannelIcon = (channel, className) => {
  const c = (channel || '').toLowerCase();
  if (c.includes('newsletter') || c.includes('email')) return <Mail className={className} />;
  if (c.includes('instagram')) return <Instagram className={className} />;
  if (c.includes('facebook')) return <Facebook className={className} />;
  if (c.includes('twitter') || c === 'x' || c.includes(' x ') || c.startsWith('x ')) return <Twitter className={className} />;
  if (c.includes('tiktok')) return <Music2 className={className} />;
  if (c.includes('sms') || c.includes('text')) return <MessageSquare className={className} />;
  if (c.includes('partner')) return <Users className={className} />;
  return <LinkIcon className={className} />;
};

const scopeMeta = {
  default:  { label: 'Default',   tone: 'bg-slate-700 text-slate-200' },
  event:    { label: 'This event', tone: 'bg-indigo-600/20 text-indigo-300 border border-indigo-700/40' },
  account:  { label: 'All my events', tone: 'bg-emerald-600/20 text-emerald-300 border border-emerald-700/40' },
};

function CopyButton({ text, label, testid }) {
  const [copied, setCopied] = useState(false);

  const legacyCopy = (value) => {
    // Last-resort path for iframed previews where the async Clipboard
    // API is blocked by Permissions-Policy. Uses the deprecated
    // execCommand which still works inside Emergent's preview wrapper.
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    document.body.removeChild(ta);
    return ok;
  };

  const onClick = async () => {
    let success = false;
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        success = true;
      } catch {
        // Permissions-Policy / focus issues — fall through to legacy.
        success = false;
      }
    }
    if (!success) success = legacyCopy(text);

    if (success) {
      setCopied(true);
      toast.success(`${label} copied!`);
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error('Copy failed — please select & copy manually');
    }
  };

  return (
    <Button size="sm" variant="outline" onClick={onClick} data-testid={testid}>
      {copied ? <Check className="h-3.5 w-3.5 mr-1.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
      {copied ? 'Copied' : `Copy ${label}`}
    </Button>
  );
}

function PresetRow({ preset, onDelete }) {
  const meta = scopeMeta[preset.scope] || scopeMeta.default;
  const canDelete = preset.scope !== 'default';

  return (
    <Card className="bg-card border-border" data-testid={`share-preset-${preset.preset_id}`}>
      <CardContent className="p-4 space-y-3">
        {/* header */}
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-indigo-500/15 flex items-center justify-center shrink-0">
            {renderChannelIcon(preset.channel, 'h-4 w-4 text-indigo-300')}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground truncate" data-testid={`preset-channel-${preset.preset_id}`}>
                {preset.channel}
              </h3>
              <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${meta.tone}`}>
                {meta.label}
              </span>
              <span className="text-[10px] text-muted-foreground font-mono">
                id: {preset.utm_id}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground">
              <Tag className="h-3 w-3" />
              <span><span className="text-muted-foreground/70">src=</span>{preset.utm_source}</span>
              <span><span className="text-muted-foreground/70">med=</span>{preset.utm_medium}</span>
              {preset.utm_content && (
                <span><span className="text-muted-foreground/70">content=</span>{preset.utm_content}</span>
              )}
            </div>
          </div>
          {canDelete && (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onDelete(preset)}
              data-testid={`preset-delete-${preset.preset_id}`}
              className="text-rose-400 hover:bg-rose-500/10 h-8 w-8"
              title="Delete preset"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {/* Share URL */}
        <div className="rounded-md bg-background border border-border p-2.5 flex items-center gap-2">
          <LinkIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <code
            className="text-[11px] text-foreground/80 truncate flex-1 font-mono"
            data-testid={`preset-share-url-${preset.preset_id}`}
          >
            {preset.share_url}
          </code>
          <CopyButton
            text={preset.share_url}
            label="link"
            testid={`preset-copy-link-${preset.preset_id}`}
          />
        </div>

        {/* Embed snippet */}
        <div className="rounded-md bg-background border border-border p-2.5 flex items-center gap-2">
          <CodeIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <code className="text-[11px] text-foreground/80 truncate flex-1 font-mono">
            {`<iframe src="…/embed/tickets/?${preset.utm_id ? `utm_id=${preset.utm_id}…` : ''}">`}
          </code>
          <CopyButton
            text={preset.embed_code}
            label="embed"
            testid={`preset-copy-embed-${preset.preset_id}`}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export default function ShareTrackTab({ eventId }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState({ defaults: [], event_presets: [], account_presets: [] });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ channel: '', utm_source: '', utm_medium: '', scope: 'event' });
  const [guideOpen, setGuideOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/events/${eventId}/share-presets`, { headers });
      setData(res.data || { defaults: [], event_presets: [], account_presets: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load presets');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (eventId) load(); }, [eventId, load]);

  const handleCreate = async () => {
    if (!form.channel.trim()) { toast.error('Channel name is required'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/events/${eventId}/share-presets`, {
        channel:     form.channel.trim(),
        utm_source:  form.utm_source.trim() || undefined,
        utm_medium:  form.utm_medium.trim() || undefined,
        scope:       form.scope,
      }, { headers });
      toast.success('Preset added');
      setForm({ channel: '', utm_source: '', utm_medium: '', scope: 'event' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create preset');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (preset) => {
    if (!window.confirm(`Delete the "${preset.channel}" preset?`)) return;
    try {
      await axios.delete(
        `${API}/events/${eventId}/share-presets/${preset.preset_id}?scope=${preset.scope}`,
        { headers }
      );
      toast.success('Preset removed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center" data-testid="share-track-loading">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
      </div>
    );
  }

  const allPresets = [
    ...(data.defaults || []),
    ...(data.event_presets || []),
    ...(data.account_presets || []),
  ];

  return (
    <div className="space-y-6" data-testid="share-track-tab">
      {/* Intro */}
      <Card className="bg-gradient-to-br from-indigo-950/40 to-purple-950/30 border-indigo-800/40">
        <CardContent className="p-5 flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-indigo-300 mt-0.5 shrink-0" />
          <div>
            <h3 className="font-semibold text-foreground">One-click pre-tagged share links</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Stop hand-crafting URLs. Each preset below ships with the correct
              <code className="mx-1 px-1 rounded bg-background border border-border text-[11px]">utm_*</code>
              tags and a unique short ID so every visit and paid sale
              shows up on your{' '}
              <span className="font-medium text-foreground">Widget Performance</span> dashboard
              attributed to the exact channel.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Best practices guide (collapsible) */}
      <Card className="bg-card border-border" data-testid="share-track-guide">
        <button
          type="button"
          onClick={() => setGuideOpen((v) => !v)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-muted/30 transition-colors"
          data-testid="share-track-guide-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <BookOpen className="h-4 w-4 text-amber-300" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-sm">
                How to use Share &amp; Track <span className="text-muted-foreground font-normal">— 60-second read</span>
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Best practices to get real attribution data flowing
              </p>
            </div>
          </div>
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${guideOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {guideOpen && (
          <div
            className="px-4 pb-5 pt-1 space-y-4 border-t border-border"
            data-testid="share-track-guide-body"
          >
            {/* What this does */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1.5">What this does</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Every share link below is auto-tagged with{' '}
                <code className="px-1 rounded bg-background border border-border text-[11px]">utm_*</code> parameters.
                When someone clicks it, lands on your event page, and buys a ticket, the sale is attributed back to the exact channel.
                Your <span className="font-medium text-foreground">Top Converting Channel</span> card and the
                <span className="font-medium text-foreground"> Widget Performance</span> table then tell you what&apos;s working.
              </p>
            </div>

            {/* The right way */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">The right way to use each channel</h4>
              <ul className="space-y-2.5 text-sm text-muted-foreground">
                <li className="flex items-start gap-2.5">
                  <Mail className="h-4 w-4 text-indigo-300 mt-0.5 shrink-0" />
                  <span>
                    <span className="font-medium text-foreground">Newsletter</span> — paste the link as the "Get tickets" button in your email blast.
                    Don&apos;t use the raw event URL or you&apos;ll lose the attribution.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Instagram className="h-4 w-4 text-pink-300 mt-0.5 shrink-0" />
                  <span>
                    <span className="font-medium text-foreground">Instagram bio</span> — drop this into your Linktree or your direct bio link.
                    For Stories, paste it into the sticker link.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Facebook className="h-4 w-4 text-blue-300 mt-0.5 shrink-0" />
                  <span>
                    <span className="font-medium text-foreground">Facebook / Twitter / X</span> — paste in the post body or pinned tweet, not your profile bio
                    (most social platforms strip query params from bio links).
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Music2 className="h-4 w-4 text-fuchsia-300 mt-0.5 shrink-0" />
                  <span>
                    <span className="font-medium text-foreground">TikTok</span> — link-in-bio only. The platform does not allow caption links.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <MessageSquare className="h-4 w-4 text-emerald-300 mt-0.5 shrink-0" />
                  <span>
                    <span className="font-medium text-foreground">SMS blast</span> — paste the link verbatim. Most carriers preserve query params on tap.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Users className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                  <span>
                    <span className="font-medium text-foreground">Partner site</span> — pair this with the{' '}
                    <span className="text-foreground">Copy embed</span> snippet so partners drop the entire ticket widget on their site (highest conversion).
                  </span>
                </li>
              </ul>
            </div>

            {/* Best practices */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Best practices</h4>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5">✓</span>
                  <span>
                    <span className="font-medium text-foreground">Make one preset per actual placement</span>, not per channel.
                    "Instagram bio" + "Instagram Story Oct 12" tells you which post drove the sale, not just that Instagram did.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5">✓</span>
                  <span>
                    Save reusable channels like "My Substack" or "My agency" to{' '}
                    <span className="font-medium text-foreground">All my events</span> so the tag stays consistent across events.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5">✓</span>
                  <span>
                    Send promo partners the <span className="font-medium text-foreground">Copy embed</span> snippet — it drops the
                    entire ticket purchase flow on their page and converts ~3× better than an outbound link.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-400 mt-0.5">✗</span>
                  <span>
                    Don&apos;t paste the raw event URL anywhere — it counts as &quot;direct&quot; traffic and you lose the attribution.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-400 mt-0.5">✗</span>
                  <span>
                    Don&apos;t edit the link after copying. The{' '}
                    <code className="px-1 rounded bg-background border border-border text-[11px]">utm_id</code> is the unique fingerprint.
                  </span>
                </li>
              </ul>
            </div>

            {/* Where to see results */}
            <div className="rounded-md bg-muted/30 border border-border p-3 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Where to see results: </span>
              The <span className="font-medium text-foreground">Event Dashboard → Overview</span> highlights your single
              best-converting channel. The <span className="font-medium text-foreground">Widget Performance</span> table on the
              same page shows every channel, split by impressions / clicks / conversions / CVR.
            </div>
          </div>
        )}
      </Card>

      {/* Add custom preset */}
      <Card className="bg-card border-border">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Plus className="h-4 w-4 text-indigo-300" />
            <h3 className="font-semibold text-foreground">Add custom channel</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-4">
              <Label className="text-xs text-muted-foreground">Channel label</Label>
              <Input
                placeholder="e.g. Partner: Denver Eater"
                value={form.channel}
                onChange={(e) => setForm({ ...form, channel: e.target.value })}
                data-testid="new-preset-channel"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs text-muted-foreground">utm_source <span className="opacity-60">(optional)</span></Label>
              <Input
                placeholder="auto from label"
                value={form.utm_source}
                onChange={(e) => setForm({ ...form, utm_source: e.target.value })}
                data-testid="new-preset-source"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-muted-foreground">utm_medium</Label>
              <Input
                placeholder="referral"
                value={form.utm_medium}
                onChange={(e) => setForm({ ...form, utm_medium: e.target.value })}
                data-testid="new-preset-medium"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs text-muted-foreground">Save to</Label>
              <select
                value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="new-preset-scope"
              >
                <option value="event">This event only</option>
                <option value="account">All my events</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="new-preset-save"
            >
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add preset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* All presets */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Your channels · {allPresets.length}
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="share-presets-grid">
          {allPresets.map((p) => (
            <PresetRow key={`${p.scope}-${p.preset_id}`} preset={p} onDelete={handleDelete} />
          ))}
        </div>
      </div>
    </div>
  );
}
